/*
 * 1DS JS SDK Privacy Guard plugin, 4.3.5
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* PrivacyGuardPlugin.ts
* @author Sid Dahiya (sidahiy)
* @copyright Microsoft 2020
* File containing the implementation for Privacy Guard plugin.
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { arrIndexOf, strEndsWith, throwError } from "@microsoft/1ds-core-js";
import { RegisteredFileTypesTrie } from "./RegisteredFileTypes";
/* Regular Expressions */
var _emailRegex = /([A-Z0-9_]|\.|%2E){1,50}(@|_AT_|%40)([A-Z0-9_]{1,50}(\.|_|%2E))+(COM|ORG|GOV|EDU)/;
var _directoryRegex = /((^|[^A-Z])[A-Z]:|\\)(\\\\{0,1}\w+)+/;
var _formatRegex = /((<HTML>)|(\{\\RTF)|(MIME-VERSION)|(<ASP:))/;
var PrivacyGuardInternal = /** @class */ (function () {
    function PrivacyGuardInternal() {
        var _customLocalValues = [];
        var _isEnabled = false;
        var _scanForUrls = false;
        var _enableAdvancedScans = true;
        var _systematicSampler = undefined;
        var _userNameParts = [];
        var _fileTypesTrie = new RegisteredFileTypesTrie();
        dynamicProto(PrivacyGuardInternal, this, function (_self, _base) {
            _self.initialize = function (coreConfig) {
                if (!coreConfig) {
                    throwError("PG:No Core");
                }
                else {
                    _enableAdvancedScans = coreConfig.disableAdvancedScans !== true;
                    if (coreConfig.scanForUrls !== undefined) {
                        _scanForUrls = coreConfig.scanForUrls === true;
                    }
                    if (coreConfig.context) {
                        _self.updateCommonContext(coreConfig.context);
                    }
                    _systematicSampler = coreConfig.systematicSampler;
                }
                _fileTypesTrie.init();
                _isEnabled = true;
            };
            _self.isEnabled = function () {
                return _isEnabled;
            };
            _self.setEnabled = function (enabled) {
                _isEnabled = enabled;
                return _isEnabled === enabled;
            };
            _self.updateCommonContext = function (freshCommonContext) {
                if (freshCommonContext === null) {
                    throwError("PG:No Context");
                }
                if (freshCommonContext.UserName && containsAnyAlphabets(freshCommonContext.UserName)) {
                    // User Name is not converted to Upper as it may be used in parts where case matters.
                    // For example, 'Awesome Username' is valid username, but 'awesome' should not match
                    // here.
                    generatePrettyNameParts(freshCommonContext.UserName, freshCommonContext.IgnoredNameParts);
                }
                if (freshCommonContext.UserNames && freshCommonContext.UserNames.length > 0) {
                    for (var _i = 0, _a = freshCommonContext.UserNames; _i < _a.length; _i++) {
                        var uname = _a[_i];
                        if (containsAnyAlphabets(uname)) {
                            // User Name is not converted to Upper as it may be used in parts where case matters.
                            // For example, 'Awesome Username' is valid username, but 'awesome' should not match
                            // here.
                            generatePrettyNameParts(uname, freshCommonContext.IgnoredNameParts);
                        }
                    }
                }
                if (freshCommonContext.UserAlias && containsAnyAlphabets(freshCommonContext.UserAlias)) {
                    _customLocalValues.push({
                        stringIsolatedWord: freshCommonContext.UserAlias.replace(/[^a-zA-Z]/g, "").toUpperCase(),
                        concern: 19 /* DataConcernType.UserAlias */
                    });
                }
                if (freshCommonContext.UserAliases && freshCommonContext.UserAliases.length > 0) {
                    for (var _b = 0, _c = freshCommonContext.UserAliases; _b < _c.length; _b++) {
                        var alias = _c[_b];
                        if (containsAnyAlphabets(alias)) {
                            _customLocalValues.push({
                                stringIsolatedWord: alias.replace(/[^a-zA-Z]/g, "").toUpperCase(),
                                concern: 19 /* DataConcernType.UserAlias */
                            });
                        }
                    }
                }
                if (freshCommonContext.MachineIds && freshCommonContext.MachineIds.length > 0) {
                    for (var _d = 0, _e = freshCommonContext.MachineIds; _d < _e.length; _d++) {
                        var machineId = _e[_d];
                        _customLocalValues.push({
                            stringValue: machineId.toUpperCase(),
                            concern: 14 /* DataConcernType.MachineName */
                        });
                    }
                }
                if (_enableAdvancedScans && freshCommonContext.OutOfScopeIdentifiers && freshCommonContext.OutOfScopeIdentifiers.length > 0) {
                    for (var _f = 0, _g = freshCommonContext.OutOfScopeIdentifiers; _f < _g.length; _f++) {
                        var identifier = _g[_f];
                        _customLocalValues.push({
                            regexpValue: makeIdentifierRegex(identifier.toUpperCase()),
                            concern: 15 /* DataConcernType.OutOfScopeIdentifier */
                        });
                    }
                }
            };
            function makeIdentifierRegex(identifier) {
                identifier = identifier.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
                identifier = identifier.replace(/\-/g, "-?");
                return new RegExp(identifier);
            }
            function generatePrettyNameParts(userName, ignoredNameParts) {
                var nameParts = userName.split(" ");
                for (var _i = 0, nameParts_1 = nameParts; _i < nameParts_1.length; _i++) {
                    var namePart = nameParts_1[_i];
                    if (namePart.length >= 4 &&
                        (!ignoredNameParts || (ignoredNameParts && arrIndexOf(ignoredNameParts, namePart) === -1))) {
                        _userNameParts.push(namePart);
                    }
                }
            }
            _self.getAllPrivacyConcerns = function (eventName, baseTypeName, fieldName, fieldValue) {
                var results = [];
                if ((_systematicSampler === null || _systematicSampler === void 0 ? void 0 : _systematicSampler.shouldSample()) === false) {
                    return results;
                }
                var isValueTruncated = false;
                if (fieldValue.length > 1000) { // Maximum performant length
                    fieldValue = fieldValue.substring(0, 1000);
                    isValueTruncated = true;
                }
                var characteristics = getAllTextCharacteristics(fieldValue);
                if (!characteristics.hasAlphabets) {
                    return results;
                }
                if (characteristics.hasForwardSlash && _scanForUrls && (characteristics.hasHttp || characteristics.hasFileUrl)) {
                    results.push({ Concern: 18 /* DataConcernType.Url */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                }
                if (characteristics.hasFileUrl) {
                    results.push({ Concern: 8 /* DataConcernType.FileSharingUrl */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                }
                if (characteristics.hasFileSharingConcern) {
                    results.push({ Concern: 8 /* DataConcernType.FileSharingUrl */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                }
                if (characteristics.hasSecurityAccessTokenConcern) {
                    results.push({ Concern: 22 /* DataConcernType.AccessToken */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                }
                if (characteristics.hasSecuritySignatureConcern) {
                    results.push({ Concern: 23 /* DataConcernType.Signature */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                }
                if (characteristics.hasBackslash) {
                    var concern = getFilePathConcerns(eventName, baseTypeName, fieldName, characteristics.valueToCheck);
                    if (concern) {
                        results = results.concat(concern);
                    }
                }
                if (characteristics.hasFormatChars) {
                    if (_formatRegex.test(characteristics.valueToCheck)) {
                        results.push({ Concern: 1 /* DataConcernType.Content */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                    }
                }
                if (characteristics.hasDot) {
                    var concern = checkForFiles(eventName, baseTypeName, fieldName, characteristics.valueToCheck, isValueTruncated);
                    if (concern) {
                        results.push(concern);
                    }
                }
                var hasEmailRegexMatch = false;
                if (characteristics.hasAtSign && (characteristics.hasDot || characteristics.hasUnderscore || characteristics.hasPercentage)) {
                    hasEmailRegexMatch = _emailRegex.test(characteristics.valueToCheck);
                    if (hasEmailRegexMatch) {
                        var emailMatch = characteristics.valueToCheck.match(_emailRegex)[0];
                        if (hasEmailRegexMatch) {
                            results.push({ Concern: getEmailType(emailMatch), EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                        }
                    }
                }
                var commonPrivacyContextsResult = checkForPrivacyContexts(eventName, baseTypeName, fieldName, characteristics.valueToCheck);
                if (commonPrivacyContextsResult[0] && commonPrivacyContextsResult[0].length > 0) {
                    results = results.concat(commonPrivacyContextsResult[0]);
                }
                var hasUserAlias = commonPrivacyContextsResult[1];
                if (!hasEmailRegexMatch) {
                    if (!hasUserAlias && (_userNameParts !== undefined && _userNameParts.length > 0)) {
                        var anyMatchedParts = false;
                        for (var index = 0; index < _userNameParts.length; index++) {
                            var namePart = _userNameParts[index];
                            if (!isContainedAsIsolatedWord(fieldValue, namePart)) {
                                continue;
                            }
                            anyMatchedParts = true;
                            break;
                        }
                        if (anyMatchedParts) {
                            results.push({ Concern: 21 /* DataConcernType.UserName */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                        }
                    }
                    if ((characteristics.valueToCheck.length > 10) &&
                        ((strEndsWith(characteristics.valueToCheck, "_SSPI") && !strEndsWith(characteristics.valueToCheck, "*_SSPI")) ||
                            (strEndsWith(characteristics.valueToCheck, "_AD") && !strEndsWith(characteristics.valueToCheck, "*_AD")))) {
                        results.push({ Concern: getEmailType(characteristics.valueToCheck), EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                    }
                }
                return results;
            };
            function isContainedAsIsolatedWord(value, word) {
                if ((!value || value.length === 0) || (!word || word.length === 0)) {
                    return false;
                }
                var startCharIndex = value.indexOf(word) - 1;
                var lastCharIndex = startCharIndex + word.length + 1;
                if (startCharIndex >= -1 && lastCharIndex >= 0) {
                    var nextChar = value.charCodeAt(lastCharIndex);
                    if (startCharIndex > -1) {
                        return (!isAlphaCharacter(value.charCodeAt(startCharIndex)) && !isAlphaCharacter(nextChar));
                    }
                    else {
                        return !isAlphaCharacter(nextChar);
                    }
                }
                return false;
            }
            function isAlphaCharacter(char) {
                return (char >= 65 && char <= 90) || (char >= 97 && char <= 122);
            }
            function containsAnyAlphabets(value) {
                for (var index = 0; index < value.length; index++) {
                    if (isAlphaCharacter(value.charCodeAt(index))) {
                        return true;
                    }
                }
                return false;
            }
            function checkForPrivacyContexts(eventName, baseTypeName, fieldName, upperCaseValue) {
                var concerns = [];
                var hasUserAlias = false;
                for (var index = 0; index < _customLocalValues.length; index++) {
                    var toMatch = _customLocalValues[index];
                    var stringValue = toMatch.stringValue;
                    if (stringValue && upperCaseValue.indexOf(stringValue) !== -1) {
                        concerns.push({ Concern: toMatch.concern, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                    }
                    else {
                        var stringIsolatedWord = toMatch.stringIsolatedWord;
                        if (stringIsolatedWord && isContainedAsIsolatedWord(upperCaseValue, stringIsolatedWord)) {
                            concerns.push({ Concern: toMatch.concern, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                            hasUserAlias = hasUserAlias || toMatch.concern === 19 /* DataConcernType.UserAlias */;
                        }
                        else {
                            var regexpValue = toMatch.regexpValue;
                            if (regexpValue && regexpValue.test(upperCaseValue)) {
                                concerns.push({ Concern: toMatch.concern, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                            }
                        }
                    }
                }
                return [concerns, hasUserAlias];
            }
            function getEmailType(emailMatch) {
                if (emailMatch.match(/MICROSOFT(\.|_|%2E)COM/) || emailMatch.match(/LINKEDIN(\.|_|%2E)COM/)) {
                    return 11 /* DataConcernType.InternalEmailAddress */;
                }
                return 5 /* DataConcernType.ExternalEmailAddress */;
            }
            function checkForFiles(eventName, baseTypeName, fieldName, fieldValue, isValueTruncated) {
                var previousDot = fieldValue.indexOf(".");
                while ((previousDot !== -1) && (fieldValue.length > previousDot)) {
                    var nextDot = fieldValue.indexOf(".", previousDot + 1);
                    var nextUnderscore = fieldValue.indexOf("_", previousDot + 1);
                    var nextSeparator = fieldValue.length;
                    if (nextDot > 0 && nextUnderscore > 0) {
                        nextSeparator = Math.min(nextUnderscore, nextDot);
                    }
                    else if (nextDot > 0) {
                        nextSeparator = nextDot;
                    }
                    else if (nextUnderscore > 0) {
                        nextSeparator = nextUnderscore;
                    }
                    var length = nextSeparator - previousDot;
                    if (length >= 3 && length <= 5) {
                        // Get the string after '.' so we have a shorter string to match.
                        var valueToSearch = fieldValue.substring(previousDot + 1, previousDot + length);
                        if (_fileTypesTrie.search(valueToSearch)) {
                            // Ignore false positives due to truncated strings.
                            if (!(nextDot == -1 && strEndsWith(fieldValue, valueToSearch) && isValueTruncated)) {
                                return { Concern: 7 /* DataConcernType.FileNameOrExtension */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName };
                            }
                        }
                    }
                    previousDot = nextDot;
                }
                return null;
            }
            function getFilePathConcerns(eventName, baseTypeName, fieldName, value) {
                var matches = value.match(_directoryRegex);
                if (!matches) {
                    return null;
                }
                var concerns = [];
                var match = matches[0]; // We don't want to look at the subgroups, just the first match.
                if (match.length > 4 && match.search(/:\\U00/) === -1 && match.search(/(\\)+SOFTWARE(\\)+MICROSOFT(\\)+/) === -1) {
                    concerns.push({ Concern: 4 /* DataConcernType.Directory */, EventName: eventName, BaseType: baseTypeName, FieldName: fieldName });
                }
                return concerns;
            }
            function getAllTextCharacteristics(value) {
                var props = {
                    hasAlphabets: false,
                    hasAtSign: false,
                    hasBackslash: false,
                    hasDash: false,
                    hasDot: false,
                    hasFileUrl: false,
                    hasFileSharingConcern: false,
                    hasFormatChars: false,
                    hasForwardSlash: false,
                    hasHttp: false,
                    hasPercentage: false,
                    hasSecurityAccessTokenConcern: false,
                    hasSecuritySignatureConcern: false,
                    hasUnderscore: false,
                    valueToCheck: value.toUpperCase()
                };
                var valueToCheck = props.valueToCheck;
                for (var currentCharIndex = 0; currentCharIndex < valueToCheck.length; currentCharIndex++) {
                    var currentChar = valueToCheck.charAt(currentCharIndex);
                    if (!props.hasAlphabets) {
                        props.hasAlphabets = isAlphaCharacter(valueToCheck.charCodeAt(currentCharIndex));
                    }
                    switch (currentChar) {
                        case "@":
                            if (!props.hasAtSign) {
                                props.hasAtSign = (currentCharIndex < valueToCheck.length - 3); // Nothing after `@`, so skip checking for emails.
                            }
                            break;
                        case "\\":
                            if (!props.hasBackslash) {
                                props.hasBackslash = true;
                            }
                            break;
                        case "-":
                            if (!props.hasDash) {
                                props.hasDash = true;
                            }
                            break;
                        case "\.":
                            if (!props.hasDot) {
                                props.hasDot = (currentCharIndex < valueToCheck.length - 2); // Not enough characters after `.` for checking
                            }
                            break;
                        case "<":
                        case "{":
                            if (!props.hasFormatChars) {
                                props.hasFormatChars = true;
                            }
                            break;
                        case "/":
                            if (!props.hasForwardSlash) {
                                props.hasForwardSlash = true;
                            }
                            if (props.hasHttp && !props.hasFileSharingConcern) {
                                if (currentCharIndex < (valueToCheck.length - 16)) {
                                    props.hasFileSharingConcern = isStringInValue(valueToCheck, "WOPI.ASHX/FILES", currentCharIndex + 1);
                                    if (props.hasFileSharingConcern) {
                                        currentCharIndex = currentCharIndex + 14;
                                    }
                                }
                                if (!props.hasFileSharingConcern && currentCharIndex < (valueToCheck.length - 12)) {
                                    props.hasFileSharingConcern = isStringInValue(valueToCheck, "WOPI/FILES/", currentCharIndex + 1);
                                    if (props.hasFileSharingConcern) {
                                        currentCharIndex = currentCharIndex + 10;
                                    }
                                }
                            }
                            break;
                        case "_":
                            if (!props.hasUnderscore) {
                                props.hasUnderscore = true;
                            }
                            if (!props.hasAtSign && currentCharIndex < (valueToCheck.length - 4)) {
                                props.hasAtSign = valueToCheck.charAt(currentCharIndex + 1) === "A" &&
                                    valueToCheck.charAt(currentCharIndex + 2) === "T" &&
                                    valueToCheck.charAt(currentCharIndex + 3) === "_";
                                if (props.hasAtSign) {
                                    currentCharIndex = currentCharIndex + 2;
                                }
                            }
                            break;
                        case "A":
                            if (props.hasForwardSlash && props.hasHttp && !props.hasSecurityAccessTokenConcern) {
                                if (currentCharIndex < (valueToCheck.length - 14)) {
                                    props.hasSecurityAccessTokenConcern = isStringInValue(valueToCheck, "WSACCESSKEYID", currentCharIndex + 1);
                                    if (props.hasSecurityAccessTokenConcern) {
                                        currentCharIndex = currentCharIndex + 12;
                                    }
                                }
                                if (!props.hasSecurityAccessTokenConcern && currentCharIndex < (valueToCheck.length - 12)) {
                                    props.hasSecurityAccessTokenConcern = isStringInValue(valueToCheck, "CCESS_TOKEN", currentCharIndex + 1);
                                    if (props.hasSecurityAccessTokenConcern) {
                                        currentCharIndex = currentCharIndex + 10;
                                    }
                                }
                            }
                            break;
                        case "D":
                            if (props.hasForwardSlash && props.hasHttp && !props.hasFileSharingConcern && currentCharIndex < (valueToCheck.length - 12)) {
                                props.hasFileSharingConcern = isStringInValue(valueToCheck, "ROPBOX.COM/", currentCharIndex + 1);
                                if (props.hasFileSharingConcern) {
                                    currentCharIndex = currentCharIndex + 10;
                                }
                            }
                            break;
                        case "F":
                            if (!props.hasFileUrl && currentCharIndex < (valueToCheck.length - 7)) {
                                props.hasFileUrl = isStringInValue(valueToCheck, "ILE://", currentCharIndex + 1);
                                if (props.hasFileUrl) {
                                    currentCharIndex = currentCharIndex + 5;
                                }
                            }
                            break;
                        case "%":
                            if (!props.hasPercentage) {
                                props.hasPercentage = true;
                            }
                            if (!props.hasAtSign && currentCharIndex < (valueToCheck.length - 3)) {
                                props.hasAtSign = valueToCheck.charAt(currentCharIndex + 1) === "4" &&
                                    valueToCheck.charAt(currentCharIndex + 2) === "0";
                                if (props.hasAtSign) {
                                    currentCharIndex = currentCharIndex + 1;
                                }
                            }
                            break;
                        case "M":
                            if (!props.hasFormatChars && currentCharIndex < (valueToCheck.length - 12)) {
                                props.hasFormatChars = isStringInValue(valueToCheck, "IME-VERSION", currentCharIndex + 1);
                                if (props.hasFormatChars) {
                                    currentCharIndex = currentCharIndex + 10;
                                }
                            }
                            break;
                        case "H":
                            if (!props.hasHttp && currentCharIndex < (valueToCheck.length - 8)) {
                                props.hasHttp = isStringInValue(valueToCheck, "TTP://", currentCharIndex + 1) || isStringInValue(valueToCheck, "TTPS://", currentCharIndex + 1);
                                if (props.hasHttp) {
                                    currentCharIndex = currentCharIndex + 5;
                                }
                            }
                            break;
                        case "S":
                            if (props.hasForwardSlash && props.hasHttp &&
                                !props.hasSecuritySignatureConcern && currentCharIndex < (valueToCheck.length - 9)) {
                                props.hasSecuritySignatureConcern = isStringInValue(valueToCheck, "IGNATURE", currentCharIndex + 1);
                                if (props.hasSecuritySignatureConcern) {
                                    currentCharIndex = currentCharIndex + 7;
                                }
                            }
                            break;
                    }
                }
                return props;
            }
            function isStringInValue(valueToCheck, stringToLookup, startIndex) {
                if (startIndex < 0 || (startIndex + stringToLookup.length) >= valueToCheck.length) {
                    return false;
                }
                for (var index = 0; index < stringToLookup.length; index++) {
                    if (valueToCheck[startIndex + index] !== stringToLookup[index]) {
                        return false;
                    }
                }
                return true;
            }
        });
    }
    /**
     * Starts the Privacy Guard plugin
     * @param config The core configuration.
     */
    PrivacyGuardInternal.prototype.initialize = function (coreConfig) {
        // Make sure we have a config definition for this identifier
    };
// Removed Stub for PrivacyGuardInternal.prototype.updateCommonContext.
// Removed Stub for PrivacyGuardInternal.prototype.isEnabled.
// Removed Stub for PrivacyGuardInternal.prototype.setEnabled.
// Removed Stub for PrivacyGuardInternal.prototype.getAllPrivacyConcerns.
    return PrivacyGuardInternal;
}());
export { PrivacyGuardInternal };
//# sourceMappingURL=PrivacyGuardInternal.js.map